import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import {
  PUBLIC_SITE_URL,
  adminDeleteMouse,
  adminGenerateMice,
  adminListMice,
  adminRenameTown,
  adminResetMouse,
  adminUpdateMouse,
  mouseUrl,
} from '../../../services/miceTrailService';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.secondary};
`;

const Input = styled.input`
  padding: 0.6rem 0.85rem;
  border: 2px solid rgba(16, 29, 64, 0.12);
  border-radius: 8px;
  font: inherit;
  font-size: 0.92rem;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
  &:focus { outline: none; border-color: ${(p) => p.theme.colors.primary}; }
`;

const Select = styled.select`
  padding: 0.6rem 0.85rem;
  border: 2px solid rgba(16, 29, 64, 0.12);
  border-radius: 8px;
  font: inherit;
  font-size: 0.92rem;
  background: #fff;
`;

const Btn = styled.button`
  padding: 0.62rem 1.1rem;
  border-radius: 8px;
  border: 2px solid ${(p) => (p.$danger ? '#b3261e' : p.theme.colors.primary)};
  background: ${(p) => (p.$solid ? p.theme.colors.primary : '#fff')};
  color: ${(p) => (p.$solid ? '#fff' : p.$danger ? '#b3261e' : p.theme.colors.primary)};
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const Chip = styled.div`
  min-width: 120px;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: ${(p) => p.theme.colors.offWhite};
  border: 1px solid rgba(16, 29, 64, 0.08);
  strong { display: block; font-size: 1.5rem; color: ${(p) => p.theme.colors.primary}; }
  span { font-size: 0.75rem; color: ${(p) => p.theme.colors.secondary}; text-transform: uppercase; letter-spacing: 0.05em; }
`;

const Banner = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: ${(p) => (p.$error ? '#f8d7da' : '#d1ecf1')};
  border: 1px solid ${(p) => (p.$error ? '#f5c6cb' : '#bee5eb')};
  color: ${(p) => (p.$error ? '#721c24' : '#0c5460')};
  font-size: 0.9rem;
  line-height: 1.5;
  code { background: rgba(0, 0, 0, 0.07); padding: 0.1rem 0.35rem; border-radius: 4px; }
`;

const Town = styled.section`
  margin-bottom: 1.5rem;
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 12px;
  overflow: hidden;
`;

const TownHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  background: ${(p) => p.theme.colors.offWhite};
  h4 { margin: 0; font-size: 1.15rem; color: ${(p) => p.theme.colors.primary}; }
  .count { color: ${(p) => p.theme.colors.secondary}; font-size: 0.88rem; }
  .spacer { flex: 1; }
`;

const TableWrap = styled.div`overflow-x: auto;`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
  th, td { padding: 0.6rem 0.85rem; text-align: left; vertical-align: top; border-top: 1px solid rgba(16, 29, 64, 0.07); }
  th { font-size: 0.7rem; letter-spacing: 0.06em; text-transform: uppercase; color: ${(p) => p.theme.colors.secondary}; white-space: nowrap; }
  td.actions { white-space: nowrap; }
  td small { display: block; color: #6b7280; }
  .code { font-family: ui-monospace, Menlo, monospace; font-size: 0.82rem; }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  background: ${(p) => (p.$found ? '#d4edda' : '#fff3cd')};
  color: ${(p) => (p.$found ? '#155724' : '#856404')};
`;

const SpotInput = styled.input`
  width: 100%;
  min-width: 170px;
  padding: 0.4rem 0.55rem;
  border: 1px solid rgba(16, 29, 64, 0.15);
  border-radius: 6px;
  font: inherit;
  &:focus { outline: none; border-color: ${(p) => p.theme.colors.primary}; }
`;

const LinkBtn = styled.button`
  border: 0;
  background: none;
  padding: 0.2rem 0.4rem;
  color: ${(p) => (p.$danger ? '#b3261e' : p.theme.colors.primary)};
  font-weight: 600;
  font-size: 0.82rem;
  cursor: pointer;
  text-decoration: underline;
`;

// ---------------------------------------------------------------------------
// Printing
// ---------------------------------------------------------------------------

const PrintGlobal = createGlobalStyle`
  @page { size: A4; margin: 10mm; }
  @media print {
    html, body { height: auto !important; overflow: visible !important; background: #fff !important; }
    body > *:not(#mice-print-root) { display: none !important; }
    #mice-print-root { position: static !important; inset: auto !important; overflow: visible !important; background: #fff !important; }
    #mice-print-root .no-print { display: none !important; }
    #mice-print-root .sheet { box-shadow: none !important; margin: 0 !important; }
  }
`;

const PrintRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100000;
  overflow: auto;
  background: #dfe3ea;
  color: #101d40;
  font-family: 'Inter', -apple-system, sans-serif;
`;

const PrintBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  padding: 0.85rem 1.25rem;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  .spacer { flex: 1; }
  .hint { font-size: 0.82rem; color: #4b5563; max-width: 26rem; }
`;

const Sheet = styled.div`
  width: 190mm;
  height: 277mm;
  margin: 1rem auto;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
  box-sizing: border-box;
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols}, 1fr);
  grid-auto-rows: 1fr;
  page-break-after: always;
  break-after: page;
  overflow: hidden;
  &:last-child { page-break-after: auto; break-after: auto; }
`;

const Tag = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.8mm;
  padding: 3mm;
  border: 0.3mm dashed #9aa3b2;
  text-align: center;
  break-inside: avoid;

  .brand { font-size: ${(p) => p.$brand}pt; font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase; color: #101d40; }
  .cta { font-size: ${(p) => p.$cta}pt; font-weight: 800; color: #c8102e; line-height: 1.1; }
  .qr { width: ${(p) => p.$qr}mm; height: ${(p) => p.$qr}mm; }
  .qr svg { display: block; width: 100%; height: 100%; }
  .meta { font-size: ${(p) => p.$meta}pt; font-weight: 700; color: #101d40; }
  .url { font-size: ${(p) => p.$url}pt; color: #4b5563; word-break: break-all; }
`;

const LAYOUTS = {
  small: { label: 'Small stickers (12 per page)', cols: 3, perPage: 12, qr: 40, brand: 6.5, cta: 10, meta: 8, url: 6 },
  medium: { label: 'Medium cards (6 per page)', cols: 2, perPage: 6, qr: 62, brand: 8.5, cta: 15, meta: 10, url: 7.5 },
  large: { label: 'Large signs (2 per page)', cols: 1, perPage: 2, qr: 100, brand: 11, cta: 22, meta: 14, url: 10 },
};

const Checklist = styled.div`
  width: 190mm;
  min-height: 277mm;
  margin: 1rem auto;
  padding: 8mm;
  box-sizing: border-box;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
  h2 { margin: 0 0 1mm; font-size: 16pt; }
  p { margin: 0 0 5mm; font-size: 9pt; color: #4b5563; }
  h3 { margin: 6mm 0 2mm; font-size: 12pt; break-after: avoid; }
  table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
  th, td { padding: 1.6mm 2mm; border: 0.3mm solid #9aa3b2; text-align: left; }
  tr { break-inside: avoid; }
  .tick { width: 9mm; }
  .box { display: inline-block; width: 4.5mm; height: 4.5mm; border: 0.4mm solid #101d40; }
`;

const chunk = (items, size) => {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

function PrintView({ mice, towns, baseUrl, initialTown, onClose }) {
  const [mode, setMode] = useState('qr'); // qr | checklist
  const [layout, setLayout] = useState('medium');
  const [town, setTown] = useState(initialTown || 'all');
  const [unfoundOnly, setUnfoundOnly] = useState(false);

  const selected = useMemo(
    () => mice.filter((m) => (town === 'all' || m.town === town) && (!unfoundOnly || m.status !== 'found')),
    [mice, town, unfoundOnly]
  );
  const cfg = LAYOUTS[layout];
  const pages = useMemo(() => chunk(selected, cfg.perPage), [selected, cfg.perPage]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const byTown = useMemo(() => {
    const groups = new Map();
    selected.forEach((m) => { groups.set(m.town, [...(groups.get(m.town) || []), m]); });
    return [...groups.entries()];
  }, [selected]);

  return createPortal(
    <PrintRoot id="mice-print-root">
      <PrintGlobal />
      <PrintBar className="no-print">
        <Label>Print
          <Select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="qr">QR codes</option>
            <option value="checklist">Placement checklist (staff only)</option>
          </Select>
        </Label>
        {mode === 'qr' && (
          <Label>Size
            <Select value={layout} onChange={(e) => setLayout(e.target.value)}>
              {Object.entries(LAYOUTS).map(([key, l]) => <option key={key} value={key}>{l.label}</option>)}
            </Select>
          </Label>
        )}
        <Label>Town
          <Select value={town} onChange={(e) => setTown(e.target.value)}>
            <option value="all">All towns</option>
            {towns.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Label>
        <Label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
          <input type="checkbox" checked={unfoundOnly} onChange={(e) => setUnfoundOnly(e.target.checked)} />
          Only mice not yet found
        </Label>
        <div className="spacer" />
        <div className="hint">
          {selected.length} {selected.length === 1 ? 'mouse' : 'mice'} · {mode === 'qr' ? `${pages.length} page${pages.length === 1 ? '' : 's'}. ` : ''}
          In the print dialog choose A4, scale 100% and turn headers and footers off.
        </div>
        <Btn type="button" $solid disabled={selected.length === 0} onClick={() => window.print()}>Print</Btn>
        <Btn type="button" onClick={onClose}>Close</Btn>
      </PrintBar>

      {selected.length === 0 && <p className="no-print" style={{ textAlign: 'center', padding: '3rem' }}>No mice match these choices.</p>}

      {mode === 'qr' && pages.map((page, pageIndex) => (
        <Sheet key={pageIndex} className="sheet" $cols={cfg.cols}>
          {page.map((m) => (
            <Tag key={m.id} $qr={cfg.qr} $brand={cfg.brand} $cta={cfg.cta} $meta={cfg.meta} $url={cfg.url}>
              <div className="brand">Belvoir Castle Mice Trail</div>
              <div className="cta">Scan me!</div>
              <div className="qr">
                <QRCodeSVG value={mouseUrl(m.code, baseUrl)} size={512} level="M" marginSize={1} title={`QR code for ${m.town} mouse ${m.number}`} />
              </div>
              <div className="meta">{m.town} · Mouse {m.number}</div>
              <div className="url">{mouseUrl(m.code, baseUrl).replace(/^https?:\/\//, '')}</div>
            </Tag>
          ))}
        </Sheet>
      ))}

      {mode === 'checklist' && selected.length > 0 && (
        <Checklist className="sheet">
          <h2>Mice Trail placement checklist</h2>
          <p>Staff copy. Do not display. Tick each mouse once it is hidden in place.</p>
          {byTown.map(([name, list]) => (
            <div key={name}>
              <h3>{name} ({list.length})</h3>
              <table>
                <thead><tr><th className="tick">Placed</th><th>#</th><th>Code</th><th>Where it is hidden</th></tr></thead>
                <tbody>
                  {list.map((m) => (
                    <tr key={m.id}>
                      <td className="tick"><span className="box" /></td>
                      <td>{m.number}</td>
                      <td style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{m.code}</td>
                      <td>{m.hidingSpot || '\u00a0'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Checklist>
      )}
    </PrintRoot>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

// Cells that start with = + - @ can run as formulas in spreadsheets, so neutralise them.
const csvCell = (value) => {
  let text = value == null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

function downloadFoundCsv(mice) {
  const header = ['Town', 'Mouse number', 'Code', 'Hidden at', 'Mouse name', 'Found by', 'Email', 'Found with', 'Found at (UTC)', 'Newsletter opt-in'];
  const rows = mice
    .filter((m) => m.status === 'found')
    .map((m) => [m.town, m.number, m.code, m.hidingSpot, m.mouseName, m.finderName, m.finderEmail, m.withWho, m.foundAt, m.marketingOptIn ? 'Yes' : 'No']);
  const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `mice-trail-found-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MiceTrailAdmin = () => {
  const [mice, setMice] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { error, text }
  const [newTown, setNewTown] = useState('');
  const [newCount, setNewCount] = useState(10);
  const [baseUrl, setBaseUrl] = useState(PUBLIC_SITE_URL);
  const [printing, setPrinting] = useState(null); // null | { town }
  const [busy, setBusy] = useState(false);

  const fail = useCallback((err) => {
    setMessage({ error: true, text: err.message });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMice(await adminListMice());
      setMessage(null);
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }, [fail]);

  useEffect(() => { load(); }, [load]);

  const towns = useMemo(() => [...new Set(mice.map((m) => m.town))], [mice]);
  const grouped = useMemo(() => towns.map((t) => ({ name: t, list: mice.filter((m) => m.town === t) })), [towns, mice]);
  const foundCount = mice.filter((m) => m.status === 'found').length;

  const run = async (action, success) => {
    setBusy(true);
    try {
      await action();
      await load();
      if (success) setMessage({ error: false, text: success });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const create = (e) => {
    e.preventDefault();
    const town = newTown.trim();
    const count = parseInt(newCount, 10);
    if (!town) { setMessage({ error: true, text: 'Enter a town name.' }); return; }
    if (!(count >= 1 && count <= 50)) { setMessage({ error: true, text: 'Choose between 1 and 50 mice.' }); return; }
    run(async () => { await adminGenerateMice(town, count); setNewTown(''); }, `Created ${count} mice for ${town}. Print their QR codes below.`);
  };

  const saveSpot = (mouse, value) => {
    if (value === mouse.hidingSpot) return;
    run(() => adminUpdateMouse(mouse.id, { hidingSpot: value }));
  };

  const rename = (name) => {
    const to = window.prompt(`New name for "${name}"`, name);
    if (!to || !to.trim() || to.trim() === name) return;
    run(() => adminRenameTown(name, to.trim()), `Renamed ${name} to ${to.trim()}.`);
  };

  const reset = (m) => {
    const ok = window.confirm(
      `Reset ${m.town} mouse ${m.number}?\n\nThis erases the registration by ${m.finderName} (${m.mouseName}) and lets the mouse be found again. Only do this for test scans or mistakes.`
    );
    if (ok) run(() => adminResetMouse(m.id), `${m.town} mouse ${m.number} has been reset.`);
  };

  const remove = (m) => {
    if (window.confirm(`Delete ${m.town} mouse ${m.number}? Its QR code will stop working.`)) {
      run(() => adminDeleteMouse(m.id), 'Mouse deleted.');
    }
  };

  return (
    <div>
      {message && <Banner $error={message.error} role="status">{message.text}</Banner>}

      <Chips>
        <Chip><strong>{towns.length}</strong><span>Towns</span></Chip>
        <Chip><strong>{mice.length}</strong><span>Mice</span></Chip>
        <Chip><strong>{foundCount}</strong><span>Found</span></Chip>
        <Chip><strong>{mice.length - foundCount}</strong><span>Still hiding</span></Chip>
      </Chips>

      <Toolbar as="form" onSubmit={create}>
        <Label>Add a town
          <Input value={newTown} onChange={(e) => setNewTown(e.target.value)} placeholder="Town name" maxLength={120} />
        </Label>
        <Label>Mice
          <Input type="number" min="1" max="50" value={newCount} onChange={(e) => setNewCount(e.target.value)} style={{ width: 90 }} />
        </Label>
        <Btn type="submit" $solid disabled={busy}>Create mice &amp; QR codes</Btn>
      </Toolbar>

      <Toolbar>
        <Label style={{ flex: '1 1 260px' }}>QR codes point to
          <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        </Label>
        <Btn type="button" $solid disabled={mice.length === 0} onClick={() => setPrinting({ town: 'all' })}>Print QR codes</Btn>
        <Btn type="button" disabled={foundCount === 0} onClick={() => downloadFoundCsv(mice)}>Download finds (CSV)</Btn>
        <Btn type="button" disabled={loading} onClick={load}>{loading ? 'Refreshing…' : 'Refresh'}</Btn>
      </Toolbar>
      <p style={{ margin: '-0.5rem 0 1.25rem', fontSize: '0.82rem', color: '#6b7280' }}>
        Every mouse has its own QR code. Once a family registers a mouse it is ticked off here and cannot be registered again.
        Keep the printed QR sheets safe until the mice are hidden: anyone holding a code can register that mouse.
      </p>

      {mice.length === 0 && !loading && (
        <p style={{ color: '#6b7280' }}>No mice yet. Add the first town above, for example 10 mice for each of the five towns.</p>
      )}

      {grouped.map(({ name, list }) => {
        const found = list.filter((m) => m.status === 'found').length;
        return (
          <Town key={name}>
            <TownHead>
              <h4>{name}</h4>
              <span className="count">{found} of {list.length} found</span>
              <span className="spacer" />
              <Btn type="button" onClick={() => rename(name)} disabled={busy}>Rename</Btn>
              <Btn type="button" onClick={() => setPrinting({ town: name })}>Print this town</Btn>
            </TownHead>
            <TableWrap>
              <Table>
                <thead>
                  <tr><th>#</th><th>Status</th><th>Hidden at (staff note)</th><th>Found by</th><th>Mouse name</th><th>Found with</th><th>When</th><th /></tr>
                </thead>
                <tbody>
                  {list.map((m) => (
                    <tr key={m.id}>
                      <td><strong>{m.number}</strong><small className="code">{m.code}</small></td>
                      <td><Badge $found={m.status === 'found'}>{m.status === 'found' ? '✓ Found' : 'Hiding'}</Badge></td>
                      <td>
                        <SpotInput
                          defaultValue={m.hidingSpot}
                          key={`${m.id}-${m.hidingSpot}`}
                          placeholder="e.g. Window of the bakery"
                          maxLength={255}
                          onBlur={(e) => saveSpot(m, e.target.value.trim())}
                          aria-label={`Hiding place for ${name} mouse ${m.number}`}
                        />
                      </td>
                      <td>{m.finderName}{m.finderEmail && <small>{m.finderEmail}</small>}</td>
                      <td>{m.mouseName}</td>
                      <td>{m.withWho}</td>
                      <td>{formatWhen(m.foundAt)}</td>
                      <td className="actions">
                        <LinkBtn type="button" onClick={() => window.open(mouseUrl(m.code, baseUrl), '_blank', 'noopener')}>Open link</LinkBtn>
                        {m.status === 'found'
                          ? <LinkBtn type="button" $danger onClick={() => reset(m)}>Reset</LinkBtn>
                          : <LinkBtn type="button" $danger onClick={() => remove(m)}>Delete</LinkBtn>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </Town>
        );
      })}

      {printing && (
        <PrintView mice={mice} towns={towns} baseUrl={baseUrl} initialTown={printing.town} onClose={() => setPrinting(null)} />
      )}
    </div>
  );
};

export default MiceTrailAdmin;
